import { FC, useEffect } from 'react'
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useLocation,
} from 'react-router-dom'

import { PrivateRoutes } from './PrivateRoutes'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { App } from '../App'
import { getDashboardLink } from '../../utils/config'
import { PublicRoutes } from './PublicRoutes'
import Login from '../PublicPages/auth/login'
import Registration from '../PublicPages/auth/registration'
import ForgotPassword from '../PublicPages/auth/forgotpassword'
import { resolvePostLoginPath, useAuth } from '../modules/auth'

const { BASE_URL } = import.meta.env

// ----------------------
// Auth redirect helper
// ----------------------
const AuthRedirect = ({ children }: { children: React.ReactNode }) => {
  const { auth, skipLoginReturnUrl, clearSkipLoginReturnUrl } = useAuth()
  const location = useLocation()

  useEffect(() => {
    if (!auth && skipLoginReturnUrl) {
      clearSkipLoginReturnUrl()
    }
  }, [auth, skipLoginReturnUrl, clearSkipLoginReturnUrl])

  if (!auth) {
    return <>{children}</>
  }

  return <Navigate to={resolvePostLoginPath(location.search, location.state)} replace />
}

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { auth, skipLoginReturnUrl } = useAuth()
  const location = useLocation()

  if (auth) {
    return <>{children}</>
  }

  if (skipLoginReturnUrl) {
    return <Navigate to="/auth/login" replace />
  }

  const currentPath = `${location.pathname}${location.search}${location.hash}`
  const search = new URLSearchParams()
  if (currentPath && currentPath !== '/') {
    search.set('redirect', currentPath)
  }

  return (
    <Navigate
      to={{ pathname: '/auth/login', search: search.toString() }}
      state={{ from: location }}
      replace
    />
  )
}

const AppRoutes: FC = () => {
  const { auth } = useAuth()

  return (
    <BrowserRouter basename={BASE_URL}>
      <Routes>
        <Route element={<App />}>

          {/* ERROR */}
          <Route path="error/*" element={<ErrorsPage />} />

          {/* PUBLIC */}
          <Route path="/pages/*" element={<PublicRoutes />} />

          {/* LOGIN */}
          <Route
            path="/auth/login"
            element={
              <AuthRedirect>
                  <Login />
              </AuthRedirect>
            }
          />

          {/* REGISTRATION */}
          <Route
            path="/auth/registration"
            element={
              <AuthRedirect>
                  <Registration />
              </AuthRedirect>
            }
          />

          {/* FORGOT PASSWORD */}
          <Route
            path="/auth/forgot-password"
            element={
              <AuthRedirect>
                  <ForgotPassword />
              </AuthRedirect>
            }
          />

          {/* PRIVATE */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <PrivateRoutes />
              </RequireAuth>
            }
          />

          {/* DEFAULT */}
          <Route
            index
            element={
              auth ? (
                <Navigate to={getDashboardLink()} replace />
              ) : (
                <Navigate to="/pages/home" replace />
                // (() => {
                //   window.location.href = envConfig.APP_PUBLIC_URL
                //   return null
                // })()
              )
            }
          />

        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export { AppRoutes }